import { useState } from "react";
import { Button } from "@/components/ui/button";
import API, { formatApiError } from "@/lib/api";
import { ArrowsClockwise } from "@phosphor-icons/react";
import { toast } from "sonner";
import { RSpin, RSpinBlock } from "@/components/RSpin";

/**
 * Postback Logs Tab — per-conversion delivery audit. Used when a publisher
 * claims they didn't receive a postback. Filters by network + delivery
 * status. State + fetch live in the parent. Each outbound row whose
 * action_id is known supports a one-click "Refire" — admin-side has no
 * cooldown and can re-deliver even successful rows.
 */
export function PostbackLogsTab({
  publisherId,
  postbackLogs,
  loadingLogs,
  logsNetwork,
  setLogsNetwork,
  logsStatus,
  setLogsStatus,
  onRefresh,
  page = 1,
  totalPages = 0,
  total = 0,
  onPageChange,
}) {
  const [refiring, setRefiring] = useState(new Set());

  const refire = async (row) => {
    const key = row.action_id;
    if (!publisherId || !key) return;
    if (refiring.has(key)) return;
    setRefiring((s) => new Set(s).add(key));
    try {
      const { data } = await API.post(
        `/admin/publishers/${publisherId}/postback-logs/refire`,
        { action_id: key },
      );
      const newStatus = data?.log?.response_status;
      if (typeof newStatus === "number" && newStatus >= 200 && newStatus < 300) {
        toast.success(`Refire delivered (${newStatus})`);
      } else {
        toast.warning(`Refire fired but server returned ${newStatus ?? "—"}`);
      }
      if (onRefresh) onRefresh();
    } catch (err) {
      toast.error(formatApiError(err) || "Refire failed");
    } finally {
      setRefiring((s) => {
        const next = new Set(s);
        next.delete(key);
        return next;
      });
    }
  };
  return (
    <div className="border border-slate-200 rounded-md overflow-hidden">
      <div className="px-5 py-3 bg-slate-50 border-b border-slate-200 flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h3 className="text-sm font-semibold text-slate-900">Postback Logs</h3>
          <p className="text-[11px] text-slate-500 mt-0.5">
            Per-conversion delivery audit. Use this when a publisher claims they didn't receive a postback.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {[
            { k: "all", label: "All" },
            { k: "impact", label: "Impact" },
            { k: "partnerize", label: "Partnerize" },
            { k: "skimlinks", label: "Skimlinks" },
            { k: "fatcoupon", label: "FatCoupon" },
            { k: "giddyup", label: "GiddyUp" },
            { k: "rga", label: "RGA" },
          ].map((n) => (
            <button
              key={n.k}
              type="button"
              onClick={() => setLogsNetwork(n.k)}
              data-testid={`pb-logs-net-${n.k}`}
              className={`h-7 px-2.5 text-[11px] font-semibold rounded-full border transition-colors ${
                logsNetwork === n.k
                  ? "bg-[#002FA7] text-white border-[#002FA7]"
                  : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"
              }`}
            >
              {n.label}
            </button>
          ))}
          <span className="w-px h-5 bg-slate-200 mx-1" />
          {[
            { k: "all", label: "All" },
            { k: "success", label: "Success" },
            { k: "failed", label: "Failed" },
          ].map((s) => (
            <button
              key={s.k}
              type="button"
              onClick={() => setLogsStatus(s.k)}
              data-testid={`pb-logs-status-${s.k}`}
              className={`h-7 px-2.5 text-[11px] font-semibold rounded-full border transition-colors ${
                logsStatus === s.k
                  ? s.k === "failed"
                    ? "bg-red-600 text-white border-red-600"
                    : s.k === "success"
                    ? "bg-emerald-600 text-white border-emerald-600"
                    : "bg-[#002FA7] text-white border-[#002FA7]"
                  : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"
              }`}
            >
              {s.label}
            </button>
          ))}
          <Button
            size="sm"
            variant="outline"
            onClick={onRefresh}
            disabled={loadingLogs}
            className="h-7 text-[11px]"
            data-testid="pb-logs-refresh"
          >
            {loadingLogs ? (
              <span className="inline-flex items-center gap-1.5">
                <RSpin size="sm" /> Loading
              </span>
            ) : "Refresh"}
          </Button>
        </div>
      </div>

      {postbackLogs.length === 0 ? (
        loadingLogs ? (
          <RSpinBlock label="Loading postback logs" height={260} data-testid="pb-logs-spinner" />
        ) : (
          <div className="px-5 py-10 text-center text-sm text-slate-400">
            No postback events yet for this filter.
          </div>
        )
      ) : (
        <div className="max-h-[600px] overflow-auto">
          <table className="w-full text-[12px]" data-testid="pb-logs-table">
            <thead className="sticky top-0 bg-slate-50 z-10">
              <tr className="border-b border-slate-200">
                <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider text-slate-500 font-bold">Time</th>
                <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider text-slate-500 font-bold">Network</th>
                <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider text-slate-500 font-bold">Direction</th>
                <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider text-slate-500 font-bold">Action ID</th>
                <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider text-slate-500 font-bold">Conv. Status</th>
                <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider text-slate-500 font-bold">Pub Response</th>
                <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider text-slate-500 font-bold">Fired URL</th>
                <th className="text-right px-3 py-2 text-[10px] uppercase tracking-wider text-slate-500 font-bold"></th>
              </tr>
            </thead>
            <tbody>
              {postbackLogs.map((l, i) => {
                const rs = l.response_status;
                const isErr = rs === "ERROR" || (typeof rs === "number" && rs >= 400);
                const isOk = typeof rs === "number" && rs >= 200 && rs < 300;
                const respCls = isOk
                  ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                  : isErr
                  ? "bg-red-50 text-red-700 border-red-200"
                  : "bg-slate-100 text-slate-600 border-slate-200";
                const netCls = {
                  impact: "bg-slate-100 text-slate-700",
                  partnerize: "bg-indigo-50 text-indigo-700",
                  skimlinks: "bg-orange-50 text-orange-700",
                  fatcoupon: "bg-rose-50 text-rose-700",
                  giddyup: "bg-yellow-50 text-yellow-700",
                  rga: "bg-red-50 text-red-700",
                  rga_vishy: "bg-red-50 text-red-700",
                  rga_ashok: "bg-pink-50 text-pink-700",
                }[l.network] || "bg-slate-100 text-slate-500";
                const dirCls = l.direction === "inbound"
                  ? "bg-blue-50 text-blue-700"
                  : "bg-purple-50 text-purple-700";
                return (
                  <tr key={i} className="border-b border-slate-100 hover:bg-slate-50/60" data-testid={`pb-log-row-${i}`}>
                    <td className="px-3 py-2 font-mono text-slate-500 whitespace-nowrap">
                      {(l.sent_at || l.received_at || "").replace("T", " ").slice(0, 19)}
                    </td>
                    <td className="px-3 py-2">
                      <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider ${netCls}`}>
                        {l.network || "—"}
                      </span>
                    </td>
                    <td className="px-3 py-2">
                      <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-medium ${dirCls}`}>
                        {l.direction || "—"}
                      </span>
                    </td>
                    <td className="px-3 py-2 font-mono text-slate-700">{l.action_id || "—"}</td>
                    <td className="px-3 py-2 font-mono text-slate-600">{l.status || "—"}</td>
                    <td className="px-3 py-2">
                      <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[11px] font-mono font-semibold border ${respCls}`}>
                        {l.error_label || rs || "—"}
                      </span>
                      {l.error && !l.error_label && (
                        <span className="ml-1.5 text-[10px] text-red-500 italic" title={l.error}>
                          {l.error.length > 40 ? l.error.slice(0, 40) + "…" : l.error}
                        </span>
                      )}
                      {l.error_label && l.error && l.error !== l.error_label && (
                        <span className="ml-1.5 text-[10px] text-slate-500 italic" title={l.error}>
                          {l.error.length > 60 ? l.error.slice(0, 60) + "…" : l.error}
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 font-mono text-[10px] text-slate-500 max-w-[420px] truncate" title={l.postback_url || ""}>
                      {l.postback_url || "—"}
                    </td>
                    <td className="px-3 py-2 text-right">
                      {l.direction === "outbound" && l.action_id && (
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={refiring.has(l.action_id)}
                          onClick={() => refire(l)}
                          className="h-6 text-[10px] border-amber-200 text-amber-700 hover:bg-amber-50"
                          data-testid={`admin-pb-refire-${l.action_id}`}
                          title="Re-deliver this postback (admin can refire success or fail)"
                        >
                          <ArrowsClockwise size={11} className={`mr-1 ${refiring.has(l.action_id) ? "animate-spin" : ""}`} />
                          {refiring.has(l.action_id) ? "Firing…" : "Refire"}
                        </Button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {total > 50 && (
        <div className="px-4 py-2.5 border-t border-slate-200 bg-slate-50/40 flex items-center justify-between text-xs text-slate-600">
          <span className="font-mono">{total.toLocaleString()} events</span>
          <div className="flex items-center gap-1">
            <Button size="sm" variant="outline" className="h-7 text-xs" disabled={page <= 1 || loadingLogs} onClick={() => onPageChange?.(page - 1)}>
              Prev
            </Button>
            <span className="font-mono mx-2">Page {page} / {Math.max(1, totalPages)}</span>
            <Button size="sm" variant="outline" className="h-7 text-xs" disabled={page >= totalPages || loadingLogs} onClick={() => onPageChange?.(page + 1)}>
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
